import { ChangeDetectionStrategy, Component, OnDestroy, computed, inject, input, output, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';

import { AuthService } from '../../../../core/auth/auth.service';
import { ApiService } from '../../../../core/services/api.service';
import { LanguageSwitcherComponent } from '../../language-switcher/language-switcher.component';
import { ProfileSettingsComponent } from '../../profile-settings/profile-settings.component';

interface ApiResponse<T> { data: T | null; }
interface HeaderProfile {
  full_name: string;
  role_name: string | null;
  profile_picture_url: string | null;
}

@Component({
  selector: 'app-portal-header',
  standalone: true,
  imports: [RouterLink, TranslatePipe, LanguageSwitcherComponent, ProfileSettingsComponent],
  templateUrl: './portal-header.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './portal-header.component.scss',
})
export class PortalHeaderComponent implements OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly api = inject(ApiService);
  private readonly router = inject(Router);
  private profileObjectUrl: string | null = null;

  readonly sidebarCollapsed = input(false);
  readonly menuToggle = output<void>();
  private readonly profileName = signal<string | null>(null);
  private readonly profileRole = signal<string | null>(null);
  readonly userName = computed(() => this.profileName()?.trim() || this.authService.userName().trim() || 'Portal User');
  readonly roleName = computed(() => this.formatRoleName(this.profileRole() || this.authService.roleName()));
  readonly profileOpen = signal(false);
  readonly settingsMenuOpen = signal(false);
  readonly profilePhotoUrl = signal<string | null>(null);
  readonly initials = computed(() =>
    this.userName()
      .split(/[.@\s_-]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join('') || 'QU',
  );

  constructor() { this.loadProfilePhoto(); }

  openProfile(): void { this.settingsMenuOpen.set(false); this.profileOpen.set(true); }
  closeProfile(): void { this.profileOpen.set(false); this.loadProfilePhoto(); }

  toggleSettingsMenu(): void { this.settingsMenuOpen.update(open => !open); }

  profileImageFailed(): void { this.revokeProfilePhoto(); }

  ngOnDestroy(): void { this.revokeProfilePhoto(); }

  private loadProfilePhoto(): void {
    this.api.get<ApiResponse<HeaderProfile>>('/auth/me').subscribe({
      next: response => {
        this.profileName.set(response.data?.full_name?.trim() || null);
        this.profileRole.set(response.data?.role_name?.trim() || null);
        if (!response.data?.profile_picture_url) { this.revokeProfilePhoto(); return; }
        this.api.download('/auth/profile-picture').subscribe({
          next: blob => {
            this.revokeProfilePhoto();
            this.profileObjectUrl = URL.createObjectURL(blob);
            this.profilePhotoUrl.set(this.profileObjectUrl);
          },
          error: () => this.revokeProfilePhoto(),
        });
      },
      error: () => this.revokeProfilePhoto(),
    });
  }

  private revokeProfilePhoto(): void {
    if (this.profileObjectUrl) URL.revokeObjectURL(this.profileObjectUrl);
    this.profileObjectUrl = null;
    this.profilePhotoUrl.set(null);
  }

  private formatRoleName(value: string | null | undefined): string {
    const role = value?.trim().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
    if (!role) return 'Portal User';
    return role
      .split(' ')
      .map(part => /^(qci|qc|ulb)$/i.test(part) ? part.toUpperCase() : `${part[0]?.toUpperCase() ?? ''}${part.slice(1).toLowerCase()}`)
      .join(' ');
  }

  signOut(): void {
    this.authService.logoutFromServer().subscribe(() => {
      void this.router.navigate(['/auth/login']);
    });
  }

}
